import React from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from '../components/MarketingHeader';
import { REQUEST_ACCESS_URL } from '../config/links';
import LegalFooter from '../components/LegalFooter';
import useDocumentMeta from '../hooks/useDocumentMeta';

/* Public index for /resources — the essays, the FAQ, and the audience
   pages, in one browsable place. (The internal tool-inventory app that
   used to sit on this URL now lives at /app/resources.) */

const ESSAYS = [
  {
    href: '/resources/what-is-an-engineering-operating-system',
    title: 'What is an Engineering Operating System?',
    blurb: 'The category, defined: the system a hardware organization runs its design loop inside — so capability compounds instead of resetting every project.',
    tag: 'the category',
  },
  {
    href: '/resources/engineering-operating-system-vs-plm',
    title: 'Engineering Operating System vs. PLM',
    blurb: 'Different shapes of product solving different problems. What each is built for, where they overlap, and which one your team actually needs.',
    tag: 'comparison',
  },
  {
    href: '/resources/design-evidence-as-byproduct',
    title: 'Design evidence as a byproduct',
    blurb: "Documentation you have to write after the fact always loses. Evidence that falls out of doing the work always survives. How Factory OS makes the second one the default.",
    tag: 'philosophy',
  },
  {
    href: '/resources/iadt-verification-for-hardware-teams',
    title: 'IADT verification, applied to hardware teams',
    blurb: 'Inspection · Analysis · Demonstration · Test — the verification model real engineering organizations use, and how it runs at write time in Factory OS.',
    tag: 'method',
  },
];

const MORE = [
  { href: '/faq', title: 'FAQ', blurb: 'Nodes, the free tier, qualification, the notebook — the canonical answers.' },
  { href: '/start', title: 'The free notebook', blurb: 'Graph paper with a QR code on every page. Scan a sketch; it becomes a node.' },
  { href: '/machining', title: 'Machining program', blurb: 'Design it. Prove it. Then we build it for you.' },
  { href: '/security', title: 'Security & trust', blurb: 'Your IP stays yours. How we treat your engineering record.' },
];

const AUDIENCES = [
  { href: '/for/hardware-startups', title: 'For hardware startups' },
  { href: '/for/defense-aerospace', title: 'For defense & aerospace' },
  { href: '/for/university-teams', title: 'For university teams' },
];

const ResourcesIndexPage = () => {

  useDocumentMeta({
    title: 'Resources — Essays, FAQ, and programs | Factory OS',
    description:
      'Essays on the Engineering Operating System category, the comparison everyone asks for, design evidence as a byproduct, and IADT verification — plus the FAQ, the free notebook, and the machining program.',
    path: '/resources',
  });

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      <section className="pt-14 sm:pt-20 pb-12 px-5">
        <div className="max-w-[1080px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          <div className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>resources</div>
          <h1 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            Read the thinking.
          </h1>
          <p className="mt-3 max-w-[640px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
            The category, the comparison everyone asks for, and the methods underneath — written to be useful whether or not you ever use Factory OS.
          </p>

          {/* Essays */}
          <div className="mt-9 grid grid-cols-1 md:grid-cols-2 gap-4">
            {ESSAYS.map(({ href, title, blurb, tag }) => (
              <a key={href} href={href} className="nb-sketch no-underline block" style={{ padding: '18px' }}>
                <div className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>{tag}</div>
                <div className="mt-1" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', lineHeight: 1.2 }}>{title}</div>
                <div className="mt-2" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>{blurb}</div>
                <div className="mt-3" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--nb-accent)', letterSpacing: '0.4px' }}>Read →</div>
              </a>
            ))}
          </div>

          {/* Programs + FAQ */}
          <div className="mt-10">
            <div className="nb-eyebrow">more</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {MORE.map(({ href, title, blurb }) => (
                <a key={href} href={href} className="nb-sketch-thin no-underline block p-4">
                  <div style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)' }}>{title}</div>
                  <div className="mt-1" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>{blurb}</div>
                </a>
              ))}
            </div>
          </div>

          {/* Audience pages */}
          <div className="mt-10 flex flex-wrap items-baseline gap-x-5 gap-y-2">
            <span className="nb-eyebrow">who it&apos;s for</span>
            {AUDIENCES.map(({ href, title }) => (
              <a key={href} href={href} className="no-underline" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--nb-accent)' }}>
                {title} →
              </a>
            ))}
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default ResourcesIndexPage;
